package com.arena_web.ui.components.events

import androidx.compose.runtime.*
import com.arena_web.data.dto.TeamDto
import com.arena_web.data.dto.TeamInvitationDto
import com.arena_web.ui.components.buttons.PrimaryButton
import com.arena_web.ui.components.buttons.SecondaryButton
import com.arena_web.ui.components.forms.TextInput
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.toAnimation
import org.jetbrains.compose.web.css.*

@Composable
fun InviteTeamPopup(
    isOpen: Boolean,
    onClose: () -> Unit,
    searchResults: List<TeamDto>,
    invitations: List<TeamInvitationDto>,
    isSearching: Boolean,
    inviteInProgress: Boolean,
    onSearch: (String) -> Unit,
    onInvite: (teamId: String) -> Unit
) {
    if (!isOpen) return

    val t = LocalAppStrings.current
    var query by remember { mutableStateOf("") }
    val invitedTeamIds = remember(invitations) { invitations.map { it.teamId }.toSet() }
    val invitationStatusMap = remember(invitations) { invitations.associate { it.teamId to it.status } }

    // Overlay
    Box(
        modifier = Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .zIndex(1000)
            .backgroundColor(Color("rgba(0, 0, 0, 0.6)"))
            .onClick { onClose() }
            .animation(
                PopupOverlayFadeIn.toAnimation(
                    duration = Constants.ANIMATION_FAST.ms,
                    timingFunction = AnimationTimingFunction.EaseOut
                )
            ),
        contentAlignment = Alignment.Center
    ) {
        // Popup card
        Column(
            modifier = Modifier
                .sMaxWidth(520)
                .fillMaxWidth()
                .sMargin(16)
                .sBorderRadius(20)
                .overflow(Overflow.Hidden)
                .onClick { it.stopPropagation() }
                .animation(
                    PopupSlideUp.toAnimation(
                        duration = Constants.ANIMATION_NORMAL.ms,
                        timingFunction = AnimationTimingFunction.EaseOut
                    )
                )
                .styleModifier {
                    property("border", "0.3px solid transparent")
                    property(
                        "background",
                        "linear-gradient(180deg, rgba(10, 15, 30, 0.98) 0%, rgba(5, 8, 18, 0.98) 100%) padding-box, " +
                            "linear-gradient(0deg, #0A0D14, #0A0D14) padding-box, " +
                            "${AppGradients.ButtonBorderGradientPrimary} border-box"
                    )
                    property("box-shadow", "0 24px 64px rgba(0, 0, 0, 0.5)")
                }
        ) {
            // Header
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .sPadding(20, 24)
                    .styleModifier {
                        property("border-bottom", "1px solid rgba(255, 255, 255, 0.06)")
                    },
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    t.eventInviteTeamTitle,
                    modifier = Modifier
                        .sFontSize(22)
                        .fontWeight(FontWeight.SemiBold)
                        .color(AppColors.Text.Primary)
                )
                Box(
                    modifier = Modifier
                        .sSize(36)
                        .sBorderRadius(100)
                        .cursor(Cursor.Pointer)
                        .styleModifier {
                            property("background", "rgba(255, 255, 255, 0.06)")
                        }
                        .onClick { onClose() },
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        "\u00D7",
                        modifier = Modifier
                            .sFontSize(22)
                            .color(AppColors.Text.Secondary)
                    )
                }
            }

            // Search section
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .sPadding(24)
                    .sGap(16)
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth().sGap(12),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Box(modifier = Modifier.flexGrow(1)) {
                        TextInput(
                            value = query,
                            onValueChange = { query = it },
                            label = t.eventSearchTeamsLabel,
                            placeholder = t.eventSearchTeamsPlaceholder
                        )
                    }
                    PrimaryButton(
                        text = t.commonSearch,
                        onClick = {
                            if (query.isNotBlank()) onSearch(query.trim())
                        },
                        enabled = query.isNotBlank() && !isSearching
                    )
                }

                // Results area
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .maxHeight(360.px)
                        .styleModifier {
                            property("overflow-y", "auto")
                            property("scrollbar-width", "thin")
                            property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
                        }
                        .sGap(8)
                ) {
                    if (isSearching) {
                        Box(
                            modifier = Modifier.fillMaxWidth().sPadding(24),
                            contentAlignment = Alignment.Center
                        ) {
                            SpanText(
                                t.eventSearching,
                                modifier = Modifier.bodyLMedium().color(AppColors.Text.Secondary)
                            )
                        }
                    } else if (searchResults.isEmpty() && query.isNotBlank()) {
                        Box(
                            modifier = Modifier.fillMaxWidth().sPadding(24),
                            contentAlignment = Alignment.Center
                        ) {
                            SpanText(
                                t.eventNoTeamsFound,
                                modifier = Modifier.bodyLMedium().color(AppColors.Text.Secondary)
                            )
                        }
                    } else {
                        searchResults.forEach { team ->
                            val isInvited = team.id in invitedTeamIds
                            val invitationStatus = invitationStatusMap[team.id]

                            TeamSearchResultRow(
                                team = team,
                                isInvited = isInvited,
                                invitationStatus = invitationStatus,
                                inviteInProgress = inviteInProgress,
                                onInvite = { onInvite(team.id) }
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun TeamSearchResultRow(
    team: TeamDto,
    isInvited: Boolean,
    invitationStatus: String?,
    inviteInProgress: Boolean,
    onInvite: () -> Unit
) {
    val t = LocalAppStrings.current
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .sPadding(12, 16)
            .sBorderRadius(12)
            .styleModifier {
                property("background", "rgba(255, 255, 255, 0.04)")
                property("border", "1px solid rgba(255, 255, 255, 0.06)")
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Team image or placeholder
        Box(
            modifier = Modifier
                .sSize(44)
                .sBorderRadius(100)
                .overflow(Overflow.Hidden)
                .styleModifier {
                    property("background", "rgba(255, 255, 255, 0.08)")
                    property("flex-shrink", "0")
                },
            contentAlignment = Alignment.Center
        ) {
            if (team.imageUrl != null) {
                org.jetbrains.compose.web.dom.Img(
                    src = team.imageUrl,
                    attrs = {
                        style {
                            property("width", "100%")
                            property("height", "100%")
                            property("object-fit", "cover")
                        }
                    }
                )
            } else {
                Icon(
                    src = AppIcons.TeamPeople,
                    modifier = Modifier.sSize(24)
                )
            }
        }

        // Team info
        Column(
            modifier = Modifier
                .sMarginLeft(12)
                .flexGrow(1)
                .sGap(2)
                .styleModifier { property("min-width", "0") }
        ) {
            SpanText(
                team.name,
                modifier = Modifier
                    .sFontSize(15)
                    .fontWeight(FontWeight.Medium)
                    .color(AppColors.Text.Primary)
                    .styleModifier {
                        property("white-space", "nowrap")
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                    }
            )
            Row(modifier = Modifier.sGap(12)) {
                SpanText(
                    t.eventTeamRating(team.rating.toString()),
                    modifier = Modifier.sFontSize(13).color(AppColors.Text.Secondary)
                )
                SpanText(
                    t.eventTeamMembersCount(team.membersCount),
                    modifier = Modifier.sFontSize(13).color(AppColors.Text.Secondary)
                )
            }
        }

        // Action button or status badge
        Box(modifier = Modifier.sMarginLeft(12).styleModifier { property("flex-shrink", "0") }) {
            if (isInvited) {
                val badgeText = when (invitationStatus?.uppercase()) {
                    "ACCEPTED" -> t.eventInviteStatusAccepted
                    "DECLINED" -> t.eventInviteStatusDeclined
                    else -> t.eventInviteStatusInvited
                }
                val badgeColor = when (invitationStatus?.uppercase()) {
                    "ACCEPTED" -> "#44CC44"
                    "DECLINED" -> "#FF4444"
                    else -> "#FFB800"
                }
                Box(
                    modifier = Modifier
                        .sPadding(6, 14)
                        .sBorderRadius(8)
                        .styleModifier {
                            property("background", "rgba(255, 255, 255, 0.06)")
                            property("border", "1px solid $badgeColor")
                        },
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        badgeText,
                        modifier = Modifier
                            .sFontSize(13)
                            .fontWeight(FontWeight.Medium)
                            .color(Color(badgeColor))
                    )
                }
            } else {
                PrimaryButton(
                    text = t.eventInvite,
                    onClick = onInvite,
                    enabled = !inviteInProgress
                )
            }
        }
    }
}
